import React, { useState } from 'react';
import { registerPlugin, Capacitor } from '@capacitor/core';
import authService from '../services/authService';
import { onboardingHref } from '../utils/returnTo';
import './portalkit/kit.css';

const AppleSignIn = registerPlugin('AppleSignIn');

/**
 * AppleSignInButton — native Sign in with Apple.
 *
 * Unlike Google this never leaves the app: ASAuthorizationController presents
 * Apple's own sheet and hands back a signed identity token, which the server
 * verifies against Apple's public keys.
 *
 * Only rendered on native for now. The web flow needs a Services ID and a signing
 * key configured before it can work, and a button that always fails is worse than
 * no button.
 */
const SERVICES_ID = process.env.REACT_APP_APPLE_SERVICES_ID || 'com.partyresume.web';
const WEB_REDIRECT_URI = 'https://partyresume.com/auth/apple/callback';

export function appleSignInAvailable() {
  // Native: ASAuthorizationController, in-app, no redirect.
  if (Capacitor.isNativePlatform()) return Capacitor.getPlatform() === 'ios';
  // Web: only where the return URL Apple has registered can actually be reached.
  // On localhost the redirect would bounce to production and sign you in there.
  return typeof window !== 'undefined' && window.location.origin === 'https://partyresume.com';
}

export default function AppleSignInButton({ redirect = '/square', label = 'Continue with Apple', disabled, onBeforeRedirect, variant = 'slab' }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!appleSignInAvailable()) return null;

  const handleClick = async () => {
    setError(null);
    setBusy(true);
    try {
      onBeforeRedirect?.();

      if (!Capacitor.isNativePlatform()) {
        // Apple insists on response_mode=form_post once name or email are in
        // scope, so it POSTs to the return URL — server.js catches that, since
        // a static SPA can't. `state` carries where to land afterwards.
        const params = new URLSearchParams({
          client_id: SERVICES_ID,
          redirect_uri: WEB_REDIRECT_URI,
          response_type: 'code id_token',
          scope: 'name email',
          response_mode: 'form_post',
          state: redirect || '/square',
        });
        window.location.href = `https://appleid.apple.com/auth/authorize?${params.toString()}`;
        return;
      }

      const res = await AppleSignIn.authorize();

      // The user backed out of Apple's sheet — not an error to shout about.
      if (res?.canceled) { setBusy(false); return; }
      if (!res?.identityToken) { setError('Apple sign-in did not complete.'); setBusy(false); return; }

      // fullName is present ONLY on the very first authorization for this app,
      // ever. Forward whatever came back; the server uses it just for new accounts.
      const result = await authService.appleLogin(res.identityToken, res.fullName || null);
      if (!result.success) {
        setError(result.error || 'Apple sign-in failed.');
        setBusy(false);
        return;
      }

      // Sign-up v3: an account created by this sign-in walks `/onboard`; a returning one goes on.
      const isNew = !!result.data?.isNewAccount || !result.data?.user?.profile?.displayName;
      if (isNew && result.data?.suggestedDisplayName) localStorage.setItem('googleSuggestedName', result.data.suggestedDisplayName);
      window.location.href = isNew ? onboardingHref(redirect) : (redirect || '/square');
    } catch (e) {
      setError(typeof e?.message === 'string' ? e.message : 'Apple sign-in failed.');
      setBusy(false);
    }
  };

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || busy}
        className={variant === 'pill'
          ? 'pk pk-btn pk-outline-on-black w-full'
          : variant === 'enter'
            ? 'pk pk-btn pk-primary-on-black w-full'
            : 'w-full flex items-center justify-center gap-3 border-2 border-black bg-black px-4 py-3 font-mono text-sm uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-black disabled:opacity-40 disabled:cursor-not-allowed'}
        style={variant === 'pill' ? { height: 52, gap: 12, fontSize: 12, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase' }
          : variant === 'enter' ? { height: 56, gap: 12, fontSize: 12, fontWeight: 800, letterSpacing: '.16em', textTransform: 'uppercase' } : undefined}
      >
        <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M16.36 12.78c.02-2.2 1.8-3.26 1.88-3.31-1.02-1.5-2.62-1.7-3.19-1.72-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.87-.76-1.48.02-2.84.86-3.6 2.18-1.53 2.66-.39 6.6 1.1 8.76.73 1.06 1.6 2.25 2.74 2.2 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.7.71 2.87.69 1.18-.02 1.93-1.08 2.65-2.14.83-1.22 1.18-2.41 1.2-2.47-.03-.01-2.3-.88-2.32-3.52zM14.2 5.9c.6-.74 1.01-1.75.9-2.76-.87.04-1.93.58-2.56 1.31-.56.65-1.05 1.69-.92 2.68.97.08 1.97-.49 2.58-1.23z" />
        </svg>
        {busy ? 'Signing in…' : label}
      </button>
      {error && <p className="mt-2 font-mono text-xs text-red-600">{error}</p>}
    </div>
  );
}
